// User roles
export const USER_ROLES = {
  ADMIN: 'ADMIN',
  DISPATCHER: 'DISPATCHER',
  DRIVER: 'DRIVER',
  PASSENGER: 'PASSENGER',
  DOCTOR: 'DOCTOR',
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

// Whether a User row is a person or a machine credential for an external
// integration. Mirrors the Prisma `AccountType` enum.
//
// A SERVICE account signs in at POST /api/auth/token with a client id +
// secret, never at /api/auth/login, and holds no browser session — so an
// integration's token can never evict a dispatcher from the admin panel.
// It is NOT a role: service accounts carry DISPATCHER like any other staff
// principal, so every existing role gate applies to them unchanged.
export const ACCOUNT_TYPES = {
  HUMAN:   'HUMAN',
  SERVICE: 'SERVICE',
} as const;

export type AccountType = (typeof ACCOUNT_TYPES)[keyof typeof ACCOUNT_TYPES];

/**
 * Email domain used to synthesize a service account's client id.
 *
 * `User.email` is already the unique login identifier, so it doubles as the
 * client id rather than adding a second unique column. The domain is
 * non-routable on purpose — nothing should ever try to deliver mail to a
 * machine credential.
 */
export const SERVICE_ACCOUNT_EMAIL_DOMAIN = 'service.kabbi.local';

/** Access-token lifetime for a service account when unset (seconds). */
export const SERVICE_TOKEN_DEFAULT_TTL_SEC = 3600;

// Ride statuses
export const RIDE_STATUSES = {
  UNASSIGNED:         'UNASSIGNED',  // saved without vehicle/driver — pending dispatch
  REQUESTED:          'REQUESTED',
  ASSIGNED:           'ASSIGNED',
  ACCEPTED:           'ACCEPTED',
  ARRIVING:           'ARRIVING',            // driver en route to pickup
  WAITING_FOR_CLIENT: 'WAITING_FOR_CLIENT',  // driver at pickup, waiting for passenger to board
  IN_PROGRESS:        'IN_PROGRESS',         // passenger on board, driving to destination
  COMPLETED:          'COMPLETED',
  CANCELLED:          'CANCELLED',
} as const;

export type RideStatus = (typeof RIDE_STATUSES)[keyof typeof RIDE_STATUSES];

/**
 * Terminal ride statuses — a ride in one of these states is "done" and
 * cannot transition further. Use this to trigger post-completion cleanup
 * (e.g. purging cached route geometry per map provider TOS / privacy), or
 * to gate UI actions that only make sense on active rides.
 *
 * Note: soft-delete (DELETE /api/rides/:id) is implemented by setting
 * status = CANCELLED, so it's covered here. Hard-delete removes the row
 * entirely, so no status check is needed for that path.
 */
export const RIDE_TERMINAL_STATUSES = [
  RIDE_STATUSES.COMPLETED,
  RIDE_STATUSES.CANCELLED,
] as const;

export type RideTerminalStatus = (typeof RIDE_TERMINAL_STATUSES)[number];

/** True when the given status is terminal (completed or cancelled). */
export function isRideTerminal(status: string | null | undefined): status is RideTerminalStatus {
  return status === RIDE_STATUSES.COMPLETED || status === RIDE_STATUSES.CANCELLED;
}

// Ride sources
export const RIDE_SOURCES = {
  ADMIN_DISPATCH: 'ADMIN_DISPATCH',
  PUBLIC_REQUEST: 'PUBLIC_REQUEST',
} as const;

export type RideSource = (typeof RIDE_SOURCES)[keyof typeof RIDE_SOURCES];

// Driver verification statuses
export const VERIFICATION_STATUSES = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;

export type VerificationStatus =
  (typeof VERIFICATION_STATUSES)[keyof typeof VERIFICATION_STATUSES];

// User statuses
export const USER_STATUSES = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  SUSPENDED: 'SUSPENDED',
} as const;

export type UserStatus = (typeof USER_STATUSES)[keyof typeof USER_STATUSES];

// Driver online statuses
export const DRIVER_STATUSES = {
  ONLINE: 'ONLINE',
  OFFLINE: 'OFFLINE',
  BUSY: 'BUSY',
} as const;

export type DriverStatus = (typeof DRIVER_STATUSES)[keyof typeof DRIVER_STATUSES];

// Payment methods are not a static enum. Orders pick a payment from the dynamic
// PAYMENT OptionCatalog (admin-managed, seeded), the same pool vehicles
// advertise; the choice is snapshotted onto the ride. There is no payment
// integration, so no PaymentMethod / PaymentStatus enum exists.

// Scheduler event / shift lifecycle statuses
export const SHIFT_STATUSES = {
  SCHEDULED:   'SCHEDULED',
  IN_PROGRESS: 'IN_PROGRESS',
  ON_BREAK:    'ON_BREAK',
  COMPLETED:   'COMPLETED',
  CANCELLED:   'CANCELLED',
} as const;

export type ShiftStatus = (typeof SHIFT_STATUSES)[keyof typeof SHIFT_STATUSES];

// Driver work status during a shift (stored in Driver.driverStatus).
// AVAILABLE = driver is free to accept rides.
// BUSY      = driver is on an active ride or otherwise occupied.
// ON_BREAK  = driver is taking a break.
export const DRIVER_WORK_STATUSES = {
  AVAILABLE: 'AVAILABLE',
  BUSY:      'BUSY',
  ON_BREAK:  'ON_BREAK',
} as const;

export type DriverWorkStatus = (typeof DRIVER_WORK_STATUSES)[keyof typeof DRIVER_WORK_STATUSES];

// Ride cancellation reasons shown on the driver's cancel dialog.
// OTHER requires a free-text note stored separately in Ride.cancelReasonText.
export const RIDE_CANCEL_REASONS = {
  CANCELLED_BY_CUSTOMER: 'CANCELLED_BY_CUSTOMER',
  CUSTOMER_NO_SHOW:      'CUSTOMER_NO_SHOW',
  WRONG_ADDRESS:         'WRONG_ADDRESS',
  DOUBLE_BOOKED:         'DOUBLE_BOOKED',
  OTHER:                 'OTHER',
} as const;

export type RideCancelReason = (typeof RIDE_CANCEL_REASONS)[keyof typeof RIDE_CANCEL_REASONS];

// Fleet vehicle statuses (stored in FleetVehicle.status)
export const FLEET_VEHICLE_STATUSES = {
  ACTIVE:      'ACTIVE',
  MAINTENANCE: 'MAINTENANCE',
  INACTIVE:    'INACTIVE',
} as const;

export type FleetVehicleStatus = (typeof FLEET_VEHICLE_STATUSES)[keyof typeof FLEET_VEHICLE_STATUSES];

// Vehicle dispatch status (computed, returned by dispatcher API)
export const VEHICLE_DISPATCH_STATUSES = {
  ON_TOUR:  'ON_TOUR',
  ON_BREAK: 'ON_BREAK',
  WAITING:  'WAITING',
} as const;

export type VehicleDispatchStatus = (typeof VEHICLE_DISPATCH_STATUSES)[keyof typeof VEHICLE_DISPATCH_STATUSES];

// System user — used for cron jobs and automated tasks (cannot login)
export const SYSTEM_USER = {
  ID:   'SYSTEM',
  ROLE: 'SYSTEM',
} as const;

// ── Multi-org membership + unified VehicleType catalog ───────────────────────
//
// Stable keys for the three system-seeded VehicleType catalog rows. The seed
// writes these into `VehicleType.key` and marks those rows `isSystem = true`,
// so they cannot be renamed or deleted via the admin UI. Code that needs to
// match a seeded row (eligibility helpers, the bus-tour BUS lookup, fallback
// label resolution) imports from this constant rather than hardcoding the
// strings, so the match survives any future renames.
export const SEEDED_VEHICLE_TYPE_KEYS = {
  GENERAL: 'GENERAL',
  CLINIC:  'CLINIC',
  BUS:     'BUS',
} as const;

export type SeededVehicleTypeKey =
  (typeof SEEDED_VEHICLE_TYPE_KEYS)[keyof typeof SEEDED_VEHICLE_TYPE_KEYS];

// Stable machine keys for the system-seeded PAYMENT OptionCatalog rows. The
// seed writes these into `OptionCatalog.key` for the payment options and each
// ride snapshots the key alongside the labels, so code can match a payment
// method by a rename-proof key instead of its editable, localized label.
// Admin-added payment options have a null key and are treated as "other".
// Extend this set (and the seed) when a new system payment method is added.
export const PAYMENT_KEYS = {
  CASH:      'CASH',
  CARD:      'CARD',
  SEPA:      'SEPA',
  PAYPAL:    'PAYPAL',
  INVOICE:   'INVOICE',
  INSURANCE: 'INSURANCE',
} as const;

export type PaymentKey = (typeof PAYMENT_KEYS)[keyof typeof PAYMENT_KEYS];

// Payment keys for which a fare (price) is required on an order. Cash and card
// are collected/settled at ride time, so the dispatcher must enter the price;
// the other methods (SEPA, PayPal, invoice, insurance) are billed later and
// leave price optional. Add a key here to make price required for it too.
export const PRICE_REQUIRED_PAYMENT_KEYS: readonly string[] = [
  PAYMENT_KEYS.CASH,
  PAYMENT_KEYS.CARD,
];

// Payment keys that map to a PHYSICAL vehicle capability: the car must be
// equipped to take the money on the spot (a card terminal for CARD; cash
// handling for CASH). Auto-assign only requires the assigned vehicle to carry
// the payment option as a feature for these keys. The back-office billing
// methods (SEPA, PayPal, invoice, insurance) are settled later in the office
// and impose no vehicle requirement, so an order paid that way can go to any
// otherwise-eligible car. Admin-added options (null key) are non-physical too.
export const VEHICLE_CAPABILITY_PAYMENT_KEYS: readonly string[] = [
  PAYMENT_KEYS.CASH,
  PAYMENT_KEYS.CARD,
];

// Payment keys whose orders need a DOCUMENT to bill against. The office cannot
// invoice an insurer without the prescription, or a payer without the invoice —
// where CASH and CARD settle in the car and leave no paper to chase. Used to
// surface a "no document on file" warning on the order's documents surface;
// it is a WARNING, never a block (see the epic's flag-don't-block rule).
//
// PAYPAL is billed later like the three below but is deliberately absent: it
// settles electronically through the provider, so there is no document the
// office has to collect. Add it here if that ever stops being true.
export const DOCUMENT_REQUIRED_PAYMENT_KEYS: readonly string[] = [
  PAYMENT_KEYS.INSURANCE,
  PAYMENT_KEYS.INVOICE,
  PAYMENT_KEYS.SEPA,
];

// Identity of the root Organization that every user is auto-added to as a
// member. The seed creates one row with `id = KABBI_ROOT_ORG_ID` and
// `name = KABBI_ROOT_ORG_NAME`. The chip is locked in the membership editor
// and the API rejects DELETE on the Kabbi membership, so every user is
// always reachable through the root org regardless of their other memberships.
export const KABBI_ROOT_ORG_ID   = 'kabbi-root';
export const KABBI_ROOT_ORG_NAME = 'Kabbi';

// ── Permanent Tours (Bus-Tour) ──────────────────────────────────────────────
//
// Mirrors the three Prisma enums from the BusTour* models. Imported by both
// server (route handlers, materializer, cron) and admin (Tab 1 / 2 / 3) so
// the discriminator literals are written exactly once.

export const BUS_TOUR_RUN_STATUSES = {
  SCHEDULED:   'SCHEDULED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED:   'COMPLETED',
  CANCELLED:   'CANCELLED',
  MISSED:      'MISSED',
} as const;

export type BusTourRunStatus =
  (typeof BUS_TOUR_RUN_STATUSES)[keyof typeof BUS_TOUR_RUN_STATUSES];

export const BUS_TOUR_DIRECTIONS = {
  FORWARD: 'FORWARD',
  RETURN:  'RETURN',
} as const;

export type BusTourDirection =
  (typeof BUS_TOUR_DIRECTIONS)[keyof typeof BUS_TOUR_DIRECTIONS];

export const BUS_TOUR_DAYS_OF_WEEK = {
  MON: 'MON',
  TUE: 'TUE',
  WED: 'WED',
  THU: 'THU',
  FRI: 'FRI',
  SAT: 'SAT',
  SUN: 'SUN',
} as const;

export type BusTourDayOfWeek =
  (typeof BUS_TOUR_DAYS_OF_WEEK)[keyof typeof BUS_TOUR_DAYS_OF_WEEK];

// ── Recurring Trips ──────────────────────────────────────────────────────────
//
// Mirrors the Prisma enums on the RecurringTrip* models. A RecurringTrip is a
// dispatcher-authored TEMPLATE that materializes real Ride orders; these
// literals are shared by the server (routes, materializer, cron) and the admin
// UI (list badges, editor, filters) so they are written exactly once.

export const RECURRING_TRIP_STATUSES = {
  ACTIVE:    'ACTIVE',    // generating on schedule
  PAUSED:    'PAUSED',    // temporarily halted (e.g. hospital admission); no future generation
  COMPLETED: 'COMPLETED', // reached its end date
  CANCELLED: 'CANCELLED', // stopped for good (template kept as a record)
} as const;

export type RecurringTripStatus =
  (typeof RECURRING_TRIP_STATUSES)[keyof typeof RECURRING_TRIP_STATUSES];

// Why a scheduled occurrence is skipped. VACATION / HOSPITAL / MANUAL are
// dispatcher-entered exception dates; HOLIDAY is reserved for the future
// automatic public/school-holiday calendars (not wired yet — MVP is manual).
export const RECURRING_TRIP_EXCEPTION_TYPES = {
  VACATION: 'VACATION',
  HOSPITAL: 'HOSPITAL',
  MANUAL:   'MANUAL',
  HOLIDAY:  'HOLIDAY',
} as const;

export type RecurringTripExceptionType =
  (typeof RECURRING_TRIP_EXCEPTION_TYPES)[keyof typeof RECURRING_TRIP_EXCEPTION_TYPES];

// A round-trip recurring template materializes two rides per date; the leg
// discriminates them and, with the date, forms the per-ride idempotency key
// (Ride.@@unique([recurringTripId, recurringDate, recurringLeg])).
export const RECURRING_LEGS = {
  OUTBOUND: 'OUTBOUND',
  RETURN:   'RETURN',
} as const;

export type RecurringLeg = (typeof RECURRING_LEGS)[keyof typeof RECURRING_LEGS];

// Allowed "repeat every N weeks" cadences offered in the editor (1 = weekly,
// 2 = fortnightly, 3 = every three weeks). The materializer gates a date with
// `weeksSinceStart % repeatEveryWeeks === 0`.
export const RECURRING_REPEAT_WEEKS: readonly number[] = [1, 2, 3];

// Weekday catalog for the recurrence picker. `iso` is the ISO-8601 weekday
// number (1 = Monday … 7 = Sunday) stored in RecurringTrip.weekDays; `key` is
// the i18n label key (recurringTrips.weekdays.<key>). Order = Mon → Sun.
export const RECURRING_WEEKDAYS = [
  { iso: 1, key: 'mon' },
  { iso: 2, key: 'tue' },
  { iso: 3, key: 'wed' },
  { iso: 4, key: 'thu' },
  { iso: 5, key: 'fri' },
  { iso: 6, key: 'sat' },
  { iso: 7, key: 'sun' },
] as const;

// Daily generation cron (02:00) and the rolling look-ahead window used by the
// automatic materializer. Wired in the Phase-2 logic pass; declared here so the
// contract lives in one place.
export const RECURRING_TRIP_CRON_INTERVAL = '0 2 * * *';
export const RECURRING_TRIP_HORIZON_DAYS_DEFAULT = 30;

// ── Institution facility categories ──────────────────────────────────────────
//
// Mirrors the Prisma `InstitutionType` enum and the admin Institutions "Typ"
// column. English keys; German display labels (Krankenhaus / Klinik / Praxis /
// Reha / Pflegeheim) live in the admin i18n catalog.
export const INSTITUTION_TYPES = {
  HOSPITAL:     'HOSPITAL',
  CLINIC:       'CLINIC',
  PRACTICE:     'PRACTICE',
  REHAB:        'REHAB',
  NURSING_HOME: 'NURSING_HOME',
} as const;

export type InstitutionType =
  (typeof INSTITUTION_TYPES)[keyof typeof INSTITUTION_TYPES];

// ── Driver mobile device activation + PIN auth ───────────────────────────────
//
// Mirrors the Prisma `ActivationCodeStatus` enum and centralises every
// numeric tunable that the activation / PIN flow exposes (TTL on UNUSED
// codes, generation count limits, PIN length, lockout ceiling, JWT
// lifetimes). All constants live here so tests and route handlers cannot
// drift out of sync, and adjusting a knob is a one-line edit.

export const ACTIVATION_CODE_STATUSES = {
  UNUSED:      'UNUSED',
  ACTIVATED:   'ACTIVATED',
  INVALIDATED: 'INVALIDATED',
} as const;

export type ActivationCodeStatus =
  (typeof ACTIVATION_CODE_STATUSES)[keyof typeof ACTIVATION_CODE_STATUSES];

// 10 numeric digits, leading zeros allowed. The code regex enforces format
// at the API boundary (mobile activate + admin reveal lookups).
export const ACTIVATION_CODE_LENGTH = 10;
export const ACTIVATION_CODE_REGEX  = /^\d{10}$/;

// UNUSED codes live one hour. Cron deletes the row past this window.
export const ACTIVATION_CODE_TTL_MS    = 60 * 60 * 1000;
// After a NON-evergreen code is activated its `expiresAt` is extended to
// `activatedAt + this` — once past, the bound device is revoked and the driver
// must re-activate with a fresh code. Evergreen codes ignore this entirely.
export const ACTIVATION_LICENSE_DURATION_MS = 365 * 24 * 60 * 60 * 1000; // 1 year
// Cron tick cadence — every five minutes.
export const ACTIVATION_CRON_INTERVAL  = '*/5 * * * *';

// Generation form limits (enforced server-side; UI mirrors the same range).
export const ACTIVATION_GENERATE_MIN = 1;
export const ACTIVATION_GENERATE_MAX = 10;

// A device counts as "online" on the admin Devices page if it pinged the API
// within this window. The mobile app bumps `lastSeenAt` at most once per minute
// (throttled), so a 5-minute window tolerates a couple of missed heartbeats.
export const DEVICE_ONLINE_THRESHOLD_MS = 5 * 60 * 1000;

// How long BEFORE a shift's scheduled start the driver may already access the
// app / go online. The shift becomes "active" for the mobile app (and its
// status-change + stop endpoints) once `now >= startDate - this`, so a driver
// can prepare and start early. The shift row itself still flips SCHEDULED →
// IN_PROGRESS only at its real start time (the tick job), so reporting is
// unaffected — this only widens the driver-facing availability window.
export const SHIFT_EARLY_START_MS = 30 * 60 * 1000;

// Max length for the informational device-metadata strings sent at activation
// (deviceModel / osName / osVersion / brand / appVersion / userAgent). These
// are shown on the admin Devices page and NEVER used for authorization, so an
// oversized value must never fail activation: both the client (clamp before
// send) and the server (truncate on parse) cap at this length rather than
// reject. 255 because some OEM ROMs return junk far longer than the field label
// suggests — e.g. Xiaomi HyperOS returns the full ~76-char build fingerprint
// from `Device.osName` instead of "Android".
export const DEVICE_INFO_FIELD_MAX = 255;

// PIN format. Regex ensures the field is exactly 8 digits with leading
// zeros legal. Empty / null is also acceptable on writes (clears the PIN).
export const PIN_LENGTH = 8;
export const PIN_REGEX  = /^\d{8}$/;

// Wrong-PIN ceiling. The 20th miss flips `pinLockedAt` and the next
// `mobile/login` returns 423 until an admin writes a new PIN.
export const PIN_MAX_FAILED_ATTEMPTS = 20;

// Long-lived JWT issued at activation. The driver app stores it in secure
// storage and reuses it across every shift. We use a year so the token
// effectively never expires within the device's working life.
export const DEVICE_JWT_EXPIRES_IN  = '365d';
// Session JWT issued after a successful PIN login. Mobile drops the token
// on shift end / manual logout; this is a hard ceiling for the rare case
// where a driver leaves the app open across days.
export const SESSION_JWT_EXPIRES_IN = '12h';

// JWT discriminator claim. Carried in every mobile-issued JWT so the
// route preHandlers can reject the wrong kind without parsing the rest of
// the payload.
export const JWT_KINDS = {
  DEVICE:  'device',
  SESSION: 'session',
} as const;

export type JwtKind = (typeof JWT_KINDS)[keyof typeof JWT_KINDS];

// Socket.IO event names. Single source of truth for the wire contract
/**
 * Reasons the Socket.IO auth middleware refuses a handshake.
 *
 * These strings travel to the client as `connect_error.message`, and the
 * driver app matches on them EXACTLY to tell "your token is dead, log in
 * again" apart from "the network is down, keep retrying".
 *
 * ⚠️ They are a wire contract, not log text. Do not reword them without
 * updating the matcher in `kabbi-mobile/src/lib/socket.ts` — a mismatch fails
 * silently and in the dangerous direction: the driver retries a dead token
 * forever, appearing online while sending no GPS.
 */
export const SOCKET_AUTH_ERRORS = {
  /** No token supplied in the handshake. */
  NO_TOKEN:      'Authentication required',
  /** Token supplied but failed verification, or expired. */
  INVALID_TOKEN: 'Invalid or expired token',
} as const;

// between API emits and admin/mobile listeners. Mirror is kept by hand at
// `kabbi-mobile/src/shared/constants.ts` since the standalone Expo project
// can't import the workspace package directly.
export const SOCKET_EVENTS = {
  // Ride lifecycle (driver-targeted unless noted)
  RIDE_ASSIGNED:           'ride:assigned',
  RIDE_UPDATED:            'ride:updated',
  RIDE_CANCELLED:          'ride:cancelled',
  RIDE_STATUS_CHANGED:     'ride:status_changed',
  RIDE_DECLINED_CONFIRMED: 'ride:declined_confirmed',
  RIDE_AUTO_COMPLETED:     'ride:auto_completed',

  // Shift lifecycle (driver-targeted)
  SHIFT_CREATED:   'shift:created',
  SHIFT_UPDATED:   'shift:updated',
  SHIFT_DELETED:   'shift:deleted',
  SHIFT_STARTED:   'shift:started',
  SHIFT_COMPLETED: 'shift:completed',
  SHIFT_STOPPED:   'shift:stopped',

  // Scheduler (admin/dispatcher broadcast)
  SCHEDULER_UPDATED: 'scheduler:updated',
  SCHEDULER_SYNC:    'scheduler:sync',

  // Driver state + GPS
  DRIVER_STATUS_CHANGED: 'driver:status_changed',
  DRIVER_LOCATION:       'driver:location',
  DRIVER_POSITIONS:      'driver:positions',

  // Dispatcher → driver messaging (driver-targeted)
  MESSAGE_NEW:           'message:new',

  // Dispatcher panel updates
  DISPATCHER_ACTIVE_CHANGED: 'dispatcher:active_changed',
} as const;

export type SocketEvent = (typeof SOCKET_EVENTS)[keyof typeof SOCKET_EVENTS];

// ─────────────────────────────────────────────────────────────────────────
// Documents
// ─────────────────────────────────────────────────────────────────────────

// Entity kinds a document can belong to. Mirrors the Prisma `DocumentEntity`
// enum; used as the polymorphic discriminator on Document.entity and as the
// scope marker on DocumentType.entity.
export const DOCUMENT_ENTITIES = {
  DRIVER:         'DRIVER',
  VEHICLE:        'VEHICLE',
  RIDE:           'RIDE',
  ORGANIZATION:   'ORGANIZATION',
  INSTITUTION:    'INSTITUTION',
  USER:           'USER',
  RECURRING_TRIP: 'RECURRING_TRIP',
} as const;

export type DocumentEntity = (typeof DOCUMENT_ENTITIES)[keyof typeof DOCUMENT_ENTITIES];

// Review lifecycle of an uploaded document. Mirrors the Prisma
// `DocumentStatus` enum. EXPIRED is written by the daily expiry job only.
export const DOCUMENT_STATUSES = {
  PENDING:  'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED:  'EXPIRED',
} as const;

export type DocumentStatus = (typeof DOCUMENT_STATUSES)[keyof typeof DOCUMENT_STATUSES];

// Stable machine keys for the system-seeded DocumentType rows, grouped by the
// entity they attach to. Same contract as PAYMENT_KEYS: the seed writes these
// into `DocumentType.key` so code can match a type by a rename-proof key
// instead of its editable, localized label. Admin-added types have a null key
// and are treated as "other". Extend this set AND the seed together.
export const DOCUMENT_TYPE_KEYS = {
  // Driver papers
  DRIVERS_LICENSE:            'DRIVERS_LICENSE',
  PASSENGER_TRANSPORT_PERMIT: 'PASSENGER_TRANSPORT_PERMIT', // P-Schein
  MEDICAL_CERTIFICATE:        'MEDICAL_CERTIFICATE',
  POLICE_CLEARANCE:           'POLICE_CLEARANCE',           // Führungszeugnis
  WORK_PERMIT:                'WORK_PERMIT',

  // Vehicle papers
  VEHICLE_REGISTRATION:  'VEHICLE_REGISTRATION',  // Zulassungsbescheinigung I
  VEHICLE_INSURANCE:     'VEHICLE_INSURANCE',
  TUV_REPORT:            'TUV_REPORT',            // TÜV / HU
  CONCESSION:            'CONCESSION',            // Konzession
  TAXIMETER_CALIBRATION: 'TAXIMETER_CALIBRATION', // Eichschein

  // Order / ride documents
  TRANSPORT_PRESCRIPTION: 'TRANSPORT_PRESCRIPTION', // Krankenbeförderungsschein (Muster 4)
  SIGNED_RECEIPT:         'SIGNED_RECEIPT',
  SIGNATURE:              'SIGNATURE',
  INVOICE:                'INVOICE',

  // Payer / organization documents
  CONTRACT:       'CONTRACT',
  RATE_AGREEMENT: 'RATE_AGREEMENT',

  // Customer / patient documents
  INSURANCE_CARD:      'INSURANCE_CARD',
  COPAYMENT_EXEMPTION: 'COPAYMENT_EXEMPTION', // Zuzahlungsbefreiung
} as const;

export type DocumentTypeKey = (typeof DOCUMENT_TYPE_KEYS)[keyof typeof DOCUMENT_TYPE_KEYS];

// MIME types accepted on upload. This is an ALLOWLIST and the only thing that
// decides what may enter the store — do not confuse it with the extension→MIME
// map in routes/resources.ts, which exists to label downloads and validates
// nothing. The declared MIME is cross-checked against the file's magic bytes
// server-side, so renaming an executable to .pdf does not get it in.
export const DOCUMENT_ALLOWED_MIME_TYPES: readonly string[] = [
  // Scans and photos — the bulk of what arrives.
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/tiff', // what most document scanners emit by default

  // Word processing. Both the OOXML and OpenDocument sides are listed: a
  // clinic sending a contract has no reason to know which suite we run.
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/vnd.oasis.opendocument.text', // .odt
  'application/rtf', // .rtf
  'text/rtf', // .rtf — some browsers label it this way instead

  // Spreadsheets. Rate agreements and billing lists arrive as these.
  'application/vnd.ms-excel', // .xls — Windows also labels .csv this way
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.oasis.opendocument.spreadsheet', // .ods

  // Plain text.
  'text/plain', // .txt
  'text/csv', // .csv
];

// Extensions matching the MIME allowlist above, for the file picker's `accept`
// attribute.
//
// The MIME list alone is not enough there: Windows reports a MIME per file
// association, so a machine missing the Office registry entries offers a .docx
// as `application/octet-stream` and a MIME-only filter greys it out in the
// dialog. Listing extensions as well keeps the file selectable.
//
// ⚠️ This is a CONVENIENCE for the picker, not a second gate. The server
// decides what may enter the store from the MIME list plus the magic-byte
// check, and never looks at the extension — a file's name is the one thing in
// an upload the sender fully controls.
export const DOCUMENT_ALLOWED_EXTENSIONS: readonly string[] = [
  '.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.tif', '.tiff',
  '.doc', '.docx', '.odt', '.rtf',
  '.xls', '.xlsx', '.ods',
  '.txt', '.csv',
];

// Upload size ceiling in bytes (10 MB). Scanned A4 forms land well under this;
// raise it only with the single-process API in mind — the encrypt pipeline
// streams, but a larger ceiling still means more concurrent I/O per request.
export const MAX_DOCUMENT_SIZE_BYTES = 10 * 1024 * 1024;

// Lead time for the "expires soon" warning surfaced in the admin UI and the
// driver app. Shared so both surfaces agree on what "soon" means.
export const DOCUMENT_EXPIRY_WARNING_DAYS = 30;

// ─────────────────────────────────────────────────────────────────────────
// Invoice generation
// ─────────────────────────────────────────────────────────────────────────

// Primary key of the single `InvoiceCounter` row. The table holds exactly one
// row and this constant is the only value ever written to its id, so the
// counter cannot be accidentally forked into per-caller rows.
export const INVOICE_COUNTER_ID = 'INVOICE_COUNTER';

// Prefix of a generated invoice number. The full form is
// `RE-<year>-<6-digit sequence>`, e.g. "RE-2026-000042". "RE" = Rechnung.
//
// ⚠️ The year is DISPLAY only — the sequence is global and does NOT reset
// annually (client decision: one gapless series across all organizations and
// years). Do not add reset logic on the strength of the year appearing here.
export const INVOICE_NUMBER_PREFIX = 'RE';

// Zero-padding width of the sequence part.
export const INVOICE_NUMBER_PAD = 6;

// Payment keys an invoice can be generated for. INVOICE is the plain case —
// bill the organization directly — and is the one implemented end to end.
// INSURANCE billing involves German health-insurer forms and rate agreements
// and is its own slice; SEPA and PAYPAL settle through their providers.
export const INVOICE_GENERATABLE_PAYMENT_KEYS: readonly string[] = [
  PAYMENT_KEYS.INVOICE,
];

// German standard VAT rate, in percent. Taxi/medical transport can qualify for
// the reduced rate under §12(2) Nr. 10 UStG depending on distance and vehicle,
// which is a tax question rather than a code one — this is the default the
// reference implementation states on the document, and it belongs in an
// AppSetting once the client confirms their rate.
export const INVOICE_VAT_PERCENT = 19;
